import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  EditIcon,
  FileTextIcon,
  UserIcon,
  CalendarIcon,
  BuildingIcon,
  PaperclipIcon,
  LinkIcon,
  AlertCircleIcon,
  MoreHorizontalIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { NotesAndActivityPanel, PanelNote } from '../components/NotesAndActivityPanel'
import { DetailFieldGrid } from '../components/DetailFieldGrid'
import { ViewTabBar, ViewSection, ViewEmptyState } from '../components/ViewPageLayout'
import { ActivityLog, getEntityActivities, logActivity } from '../utils/activityLogger'
import { resolveOwnerContact } from '../lib/resolveOwnerContact'
import { getServiceTypeLabel, resolveRequestServiceType } from '../lib/serviceTypeOptions'
import {
  parseLeadProjects,
  resolveLeadProject,
  type ProjectItem,
} from '../lib/propertyProjectSearch'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'

type ViewTab = 'overview' | 'details' | 'documents' | 'notes'

function parseRequestNotes(raw: unknown): PanelNote[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.map((n: any) => ({
      note: n.note,
      timestamp: n.timestamp || n.created_at,
      created_by_name: n.created_by_name,
    }))
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed)
        ? parsed.map((n: any) => ({
            note: n.note,
            timestamp: n.timestamp || n.created_at,
            created_by_name: n.created_by_name,
          }))
        : []
    } catch {
      return []
    }
  }
  return []
}

function isValidUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

export default function ProposalRequestView() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [request, setRequest] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [customer, setCustomer] = useState<any>(null)
  const [lead, setLead] = useState<any>(null)
  const [property, setProperty] = useState<any>(null)
  const [leadProjects, setLeadProjects] = useState<ProjectItem[]>([])
  const [activeTab, setActiveTab] = useState<ViewTab>('overview')
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [serviceCategories, setServiceCategories] = useState<Array<{ id: string; name: string }>>([])
  const [requestNotes, setRequestNotes] = useState<PanelNote[]>([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)

  const viewReturnToUrl = useMemo(
    () => encodeURIComponent(`/proposal-requests/view/${id}${returnTo ? `?returnTo=${returnTo}` : ''}`),
    [id, returnTo]
  )

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('service_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('name')
      setServiceCategories(data || [])
    }
    fetchCategories()
  }, [])

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'overview' || tabParam === 'details' || tabParam === 'documents' || tabParam === 'notes') {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  useEffect(() => {
    if (id) {
      fetchRequest()
    }
  }, [id])

  useEffect(() => {
    if (!id) return
    const loadActivities = async () => {
      setActivitiesLoading(true)
      try {
        const acts = await getEntityActivities('proposal', id)
        setActivities(acts)
      } finally {
        setActivitiesLoading(false)
      }
    }
    loadActivities()
  }, [id])

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd = {
        note: newNote.trim(),
        timestamp: new Date().toISOString(),
      }
      const updatedNotes = [
        noteToAdd,
        ...requestNotes.map((n) => ({
          note: n.note,
          timestamp: n.timestamp || n.created_at,
          created_by_name: n.created_by_name,
        })),
      ]

      const { error } = await supabase
        .from('proposals')
        .update({ notes: updatedNotes })
        .eq('id', id)
        .eq('record_type', 'request')

      if (error) throw error

      setRequestNotes(parseRequestNotes(updatedNotes))
      setRequest((prev: typeof request) => (prev ? { ...prev, notes: updatedNotes } : prev))
      setNewNote('')

      await logActivity({
        activity_type: 'note_added',
        entity_type: 'proposal',
        entity_id: id,
        description: 'Note added to proposal request',
      })
      const acts = await getEntityActivities('proposal', id)
      setActivities(acts)
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  const fetchRequest = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('id', id)
        .eq('record_type', 'request')
        .single()

      if (error) {
        const { data: converted, error: convertedError } = await supabase
          .from('proposals')
          .select('id, record_type')
          .eq('id', id)
          .single()

        if (!convertedError && converted?.record_type === 'proposal') {
          navigate(`/proposals/view/${id}`, { replace: true })
          return
        }
        throw error
      }
      setRequest(data)
      setRequestNotes(parseRequestNotes(data.notes))

      if (data.customer_id) {
        const { data: customerData } = await supabase
          .from('customers')
          .select('id, company_name, contact_first_name, contact_last_name, email, phone')
          .eq('id', data.customer_id)
          .single()
        setCustomer(customerData)
      } else {
        setCustomer(null)
      }

      if (data.lead_id) {
        const { data: leadData } = await supabase
          .from('leads')
          .select(
            'id, company_name, contact_first_name, contact_last_name, email, phone, projects'
          )
          .eq('id', data.lead_id)
          .single()
        setLead(leadData)
        setLeadProjects(parseLeadProjects(leadData?.projects))
      } else {
        setLead(null)
        setLeadProjects([])
      }

      if (data.property_id && isValidUUID(data.property_id)) {
        const { data: propertyData } = await supabase
          .from('properties')
          .select('id, name, address, city, state, zip_code')
          .eq('id', data.property_id)
          .single()
        setProperty(propertyData)
      } else {
        setProperty(null)
      }
    } catch (error) {
      console.error('Error fetching proposal request:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    navigate(`/proposal-requests/edit/${id}?returnTo=${viewReturnToUrl}`)
  }

  const handleCreateProposal = () => {
    if (!request) return
    navigate(`/proposals/create?requestId=${request.id}&returnTo=${viewReturnToUrl}`)
  }

  const formatPhoneLink = (phone: string | null | undefined) => {
    if (!phone || phone === 'N/A') return 'N/A'
    const cleanPhone = phone.replace(/[^\d+]/g, '')
    return (
      <a href={`tel:${cleanPhone}`} className="text-blue-600 hover:text-blue-800 underline">
        {phone}
      </a>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800'
      case 'submitted':
        return 'bg-blue-100 text-blue-800'
      case 'reviewing':
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'declined':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  if (!request) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Proposal request not found</p>
      </MobilePage>
    )
  }

  const attachments =
    request.attachments && Array.isArray(request.attachments) ? request.attachments : []
  const blueprintLinks =
    request.blueprint_links && Array.isArray(request.blueprint_links) ? request.blueprint_links : []

  const matchedLeadProject = lead
    ? resolveLeadProject(leadProjects, request.lead_project_id, request.property_address)
    : null

  const displayContact = resolveOwnerContact({
    customer,
    lead,
    property,
    leadProject: matchedLeadProject,
  })

  const projectDisplayLabel =
    request.title?.trim() ||
    matchedLeadProject?.address?.trim() ||
    property?.name ||
    property?.address ||
    request.property_address ||
    'N/A'

  const highlightProjectId =
    request.lead_project_id || matchedLeadProject?.id || undefined

  const ownerLink = customer
    ? {
        label: customer.company_name || `${customer.contact_first_name} ${customer.contact_last_name}`,
        onClick: () =>
          navigate(`/customers/view/${customer.id}?returnTo=${viewReturnToUrl}`),
      }
    : lead
      ? {
          label: lead.company_name || `${lead.contact_first_name} ${lead.contact_last_name}`,
          onClick: () => navigate(`/leads/view/${lead.id}?returnTo=${viewReturnToUrl}`),
        }
      : null

  const siteLink = property
    ? {
        label: property.name || property.address || 'N/A',
        onClick: () =>
          navigate(`/properties/view/${property.id}?returnTo=${viewReturnToUrl}`),
      }
    : lead && (matchedLeadProject || request.property_address)
      ? {
          label: projectDisplayLabel,
          onClick: () => {
            const highlight = highlightProjectId
              ? `&highlightProject=${encodeURIComponent(String(highlightProjectId))}`
              : ''
            navigate(
              `/leads/view/${lead.id}?tab=projects${highlight}&returnTo=${viewReturnToUrl}`
            )
          },
        }
      : null

  const siteFieldLabel = customer || property ? 'Property' : lead ? 'Project' : 'Project / Property'

  const backLabel =
    returnTo && returnTo.includes('/proposals/view')
      ? 'Back to Proposal'
      : returnTo && returnTo.includes('/customers/view')
        ? 'Back to Customer'
        : returnTo && returnTo.includes('/leads/view')
          ? 'Back to Lead'
          : returnTo && returnTo.includes('/properties/view')
            ? 'Back to Property'
            : returnTo
              ? 'Back'
              : 'Back to Proposals'

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/proposals')
    }
  }

  const tabBodyClass =
    activeTab === 'notes'
      ? 'p-3 sm:p-6 flex flex-col flex-1 min-h-0 overflow-hidden h-[calc(100vh-13rem)] max-h-[calc(100vh-13rem)]'
      : 'p-3 sm:p-6'

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={request.title || 'Untitled Request'}
        subtitle={`Request #${request.request_number}`}
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <button
            type="button"
            onClick={() => setActionsOpen(true)}
            className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
            aria-label="More actions"
          >
            <MoreHorizontalIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Actions</span>
          </button>
        }
      />

      <div className={`${mobile.surface} flex flex-col min-h-0`}>
        <ViewTabBar
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'details', label: 'Details' },
            {
              id: 'documents',
              label: 'Docs',
              full: 'Documents',
              count: attachments.length + blueprintLinks.length,
            },
            { id: 'notes', label: 'Notes', full: 'Notes & Activity' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        <div className={tabBodyClass}>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <ViewSection
                  title="Request Information"
                  icon={<FileTextIcon className="w-6 h-6" />}
                  tone="blue"
                >
                  <DetailFieldGrid
                    columns={4}
                    fields={[
                      { label: 'Request Number', value: request.request_number || 'N/A' },
                      {
                        label: 'Status',
                        value: (
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}
                          >
                            {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) ||
                              'Submitted'}
                          </span>
                        ),
                      },
                      {
                        label: 'Submitted Date',
                        value: request.submitted_at
                          ? new Date(request.submitted_at).toLocaleDateString()
                          : 'N/A',
                      },
                      {
                        label: 'Converted Date',
                        value: request.converted_at
                          ? new Date(request.converted_at).toLocaleDateString()
                          : undefined,
                        hidden: !request.converted_at,
                      },
                    ]}
                  />
                </ViewSection>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ViewSection
                    title={customer ? 'Customer' : lead ? 'Lead' : 'Customer / Lead'}
                    icon={<BuildingIcon className="w-6 h-6" />}
                    tone="teal"
                  >
                    {ownerLink ? (
                      <button
                        type="button"
                        onClick={ownerLink.onClick}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {ownerLink.label}
                      </button>
                    ) : (
                      <p className="text-sm text-gray-900">N/A</p>
                    )}
                  </ViewSection>

                  <ViewSection
                    title={siteFieldLabel}
                    icon={<BuildingIcon className="w-6 h-6" />}
                    tone="teal"
                  >
                    {siteLink ? (
                      <button
                        type="button"
                        onClick={siteLink.onClick}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {siteLink.label}
                      </button>
                    ) : (
                      <p className="text-sm text-gray-900">{projectDisplayLabel}</p>
                    )}
                  </ViewSection>
                </div>

                <ViewSection
                  title="Contact"
                  icon={<UserIcon className="w-6 h-6" />}
                  tone="green"
                >
                  <DetailFieldGrid
                    columns={4}
                    fields={[
                      {
                        label: 'Contact Name',
                        value: displayContact.contact_name || 'N/A',
                      },
                      {
                        label: 'Contact Email',
                        value: displayContact.contact_email ? (
                          <a
                            href={`mailto:${displayContact.contact_email}`}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {displayContact.contact_email}
                          </a>
                        ) : (
                          'N/A'
                        ),
                      },
                      {
                        label: 'Contact Phone',
                        value: displayContact.contact_phone
                          ? formatPhoneLink(displayContact.contact_phone)
                          : undefined,
                        hidden: !displayContact.contact_phone,
                      },
                      {
                        label: 'Company Name',
                        value: displayContact.company_name,
                        hidden: !displayContact.company_name,
                      },
                    ]}
                  />
                </ViewSection>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ViewSection title="Project Scope" icon={<FileTextIcon className="w-6 h-6" />} tone="blue">
                  <DetailFieldGrid
                    columns={2}
                    fields={[
                      {
                        label: 'Service Type',
                        value:
                          getServiceTypeLabel(
                            resolveRequestServiceType(request, serviceCategories)
                          ) || 'N/A',
                        hidden: !resolveRequestServiceType(request, serviceCategories),
                      },
                      {
                        label: 'Property Type',
                        value:
                          request.property_type
                            ? request.property_type.charAt(0).toUpperCase() +
                              request.property_type.slice(1)
                            : undefined,
                        hidden: !request.property_type,
                      },
                      {
                        label: 'Square Footage',
                        value: request.square_footage
                          ? `${request.square_footage.toLocaleString()} sq ft`
                          : undefined,
                        hidden: !request.square_footage,
                      },
                      {
                        label: 'Estimated Budget',
                        value: request.estimated_budget
                          ? `$${request.estimated_budget.toLocaleString()}`
                          : undefined,
                        hidden: !request.estimated_budget,
                      },
                    ]}
                  />
                  {request.description && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                      <div className="max-h-48 overflow-y-auto">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                          {request.description}
                        </p>
                      </div>
                    </div>
                  )}
                </ViewSection>

                <div className="space-y-6">
                  <ViewSection
                    title="Timeline"
                    icon={<CalendarIcon className="w-6 h-6" />}
                    tone="sky"
                  >
                    <DetailFieldGrid
                      columns={2}
                      fields={[
                        {
                          label: 'Preferred Start Date',
                          value: request.preferred_start_date
                            ? new Date(request.preferred_start_date).toLocaleDateString()
                            : undefined,
                          hidden: !request.preferred_start_date,
                        },
                        {
                          label: 'Preferred Completion Date',
                          value: request.preferred_completion_date
                            ? new Date(request.preferred_completion_date).toLocaleDateString()
                            : undefined,
                          hidden: !request.preferred_completion_date,
                        },
                        {
                          label: 'Urgency Level',
                          value:
                            request.urgency_level?.charAt(0).toUpperCase() +
                              request.urgency_level?.slice(1) || 'Normal',
                        },
                      ]}
                    />
                  </ViewSection>

                  {(request.special_requirements ||
                    request.accessibility_needs ||
                    request.existing_contractor) && (
                    <ViewSection
                      title="Special Requirements"
                      icon={<AlertCircleIcon className="w-6 h-6" />}
                      tone="amber"
                    >
                      {request.special_requirements && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Special Requirements
                          </p>
                          <div className="max-h-36 overflow-y-auto">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                              {request.special_requirements}
                            </p>
                          </div>
                        </div>
                      )}
                      {request.accessibility_needs && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Access Info</p>
                          <div className="max-h-36 overflow-y-auto">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                              {request.accessibility_needs}
                            </p>
                          </div>
                        </div>
                      )}
                      {request.existing_contractor && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Existing Contractor
                          </p>
                          <p className="text-sm text-gray-900">Yes</p>
                          {request.contractor_details && (
                            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1 leading-relaxed">
                              {request.contractor_details}
                            </p>
                          )}
                        </div>
                      )}
                    </ViewSection>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ViewSection
                  title={`File Attachments (${attachments.length})`}
                  icon={<PaperclipIcon className="w-6 h-6" />}
                  tone="indigo"
                  className={attachments.length === 0 ? 'lg:col-span-2' : ''}
                >
                  {attachments.length > 0 ? (
                    <div className="space-y-2">
                      {attachments.map((attachment: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-200"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <PaperclipIcon className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.name || 'Untitled'}
                              </p>
                              {attachment.size && (
                                <p className="text-xs text-gray-500">
                                  {(attachment.size / 1024).toFixed(2)} KB
                                  {attachment.type && ` • ${attachment.type}`}
                                </p>
                              )}
                            </div>
                          </div>
                          {attachment.url && (
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 shrink-0 ml-2"
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ViewEmptyState
                      tone="indigo"
                      icon={<PaperclipIcon className="w-10 h-10 text-indigo-300" />}
                      message="No file attachments"
                    />
                  )}
                </ViewSection>

                <ViewSection
                  title={`Blueprint Links (${blueprintLinks.length})`}
                  icon={<LinkIcon className="w-6 h-6" />}
                  tone="cyan"
                  className={blueprintLinks.length === 0 && attachments.length === 0 ? 'lg:col-span-2' : ''}
                >
                  {blueprintLinks.length > 0 ? (
                    <div className="space-y-2">
                      {blueprintLinks.map((link: any, index: number) => (
                        <div
                          key={index}
                          className="p-2.5 bg-white rounded-lg border border-slate-200"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                              >
                                {link.title || 'Untitled Link'}
                              </a>
                              {link.description && (
                                <p className="text-xs text-gray-600 mt-0.5">{link.description}</p>
                              )}
                            </div>
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 shrink-0"
                            >
                              Open
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ViewEmptyState
                      tone="cyan"
                      icon={<LinkIcon className="w-10 h-10 text-cyan-300" />}
                      message="No blueprint links"
                    />
                  )}
                </ViewSection>
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={requestNotes}
                activities={activities}
                activitiesLoading={activitiesLoading}
                notesTitle="Request Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
                layout="split"
                compact
                className="flex-1 min-h-0"
                beforePanels={
                  request.internal_notes || request.review_notes ? (
                    <details className="bg-gray-50 rounded-lg border border-gray-200 p-3 shrink-0">
                      <summary className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                        Staff fields (internal & review notes)
                      </summary>
                      <p className="text-xs text-gray-500 mt-2 mb-3">
                        Internal notes are staff-only. Review notes capture feedback during internal
                        review. Timestamped request notes are below.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        {request.internal_notes && (
                          <div className="bg-white rounded-lg border border-gray-200 p-3">
                            <h3 className="text-xs font-medium text-gray-600 mb-1">Internal Notes</h3>
                            <p className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">
                              {request.internal_notes}
                            </p>
                          </div>
                        )}
                        {request.review_notes && (
                          <div className="bg-white rounded-lg border border-gray-200 p-3">
                            <h3 className="text-xs font-medium text-gray-600 mb-1">Review Notes</h3>
                            <p className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">
                              {request.review_notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </details>
                  ) : null
                }
              />
            )}
        </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          <button type="button" onClick={handleEdit} className={mobile.secondaryBtn}>
            <EditIcon className="w-4 h-4" />
            Edit
          </button>
          {request.status !== 'converted' && (
            <button type="button" onClick={handleCreateProposal} className={mobile.primaryBtn}>
              <FileTextIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Create Proposal</span>
              <span className="sm:hidden">Proposal</span>
            </button>
          )}
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Request actions">
        <div className="space-y-2">
          {request.status !== 'converted' && (
            <button
              type="button"
              onClick={() => {
                setActionsOpen(false)
                handleCreateProposal()
              }}
              className={`${mobile.primaryBtn} w-full`}
            >
              <FileTextIcon className="w-4 h-4" />
              Create Proposal
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleEdit()
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Request
          </button>
        </div>
      </MobileSheet>
    </MobilePage>
  )
}
